import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DataTable from '../../components/DataTable';
import BulkActions from '../../components/BulkActions';
import ColumnConfig from '../../components/ColumnConfig';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Plus,
  GraduationCap,
  Mail,
  Phone,
  Eye,
  DollarSign,
  Calendar,
  Clock,
  MapPin,
  Pencil,
  Save,
  UserX,
  UserCheck,
  RotateCcw,
} from 'lucide-react';

const Coaches = () => {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [coachClasses, setCoachClasses] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  
  // Deactivate confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [coachToDelete, setCoachToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    display_name: '',
    hourly_rate: 0,
    experience_years: 0,
    specialization: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip_code: ''
    },
    emergency_contact: {
      name: '',
      phone: '',
      relationship: ''
    }
  });

  const fetchCoaches = async () => {
    try {
      // Fetch all coaches (including inactive) and filter on frontend
      const response = await api.get('/coaches?status=all');
      setCoaches(response.data);
    } catch (error) {
      toast.error('Failed to fetch coaches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoaches();
  }, []);

  // Filter coaches by status tab
  const getFilteredCoaches = () => {
    if (activeTab === 'all') return coaches;
    return coaches.filter(c => (c.profile?.status || 'active') === activeTab);
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', phone: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/coaches', { ...formData, role: 'coach' });
      toast.success('Coach added successfully');
      setDialogOpen(false);
      resetForm();
      fetchCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add coach');
    }
  };

  const handleDeactivate = async (id) => {
    setDeleting(true);
    try {
      await api.delete(`/coaches/${id}`);
      toast.success('Coach deactivated');
      fetchCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate coach');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setCoachToDelete(null);
    }
  };

  const handleReactivate = async (id) => {
    try {
      await api.put(`/coaches/${id}/reactivate`);
      toast.success('Coach reactivated');
      fetchCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate coach');
    }
  };

  const openDeleteDialog = (coach) => {
    setCoachToDelete(coach);
    setDeleteDialogOpen(true);
  };

  const _viewCoachDetails = async (coach) => {
    setSelectedCoach(coach);
    setDetailsDialog(true);
    setLoadingDetails(true);
    
    try {
      const classesRes = await api.get('/classes');
      const coachClassList = classesRes.data.filter(c => c.coach_id === coach.id);
      setCoachClasses(coachClassList);
    } catch (error) {
      console.error('Failed to load classes:', error);
      setCoachClasses([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const openEditDialog = (coach) => {
    setSelectedCoach(coach);
    const profile = coach.profile || {};
    const address = profile.address || {};
    const emergency = profile.emergency_contact || {};
    
    setEditForm({
      name: coach.name || '',
      phone: coach.phone || '',
      display_name: profile.display_name || '',
      hourly_rate: profile.hourly_rate || 0,
      experience_years: profile.experience_years || 0,
      specialization: profile.specialization || '',
      address: {
        street: typeof address === 'object' ? (address.street || '') : '',
        city: typeof address === 'object' ? (address.city || '') : '',
        state: typeof address === 'object' ? (address.state || '') : '',
        zip_code: typeof address === 'object' ? (address.zip_code || '') : ''
      },
      emergency_contact: {
        name: typeof emergency === 'object' ? (emergency.name || '') : '',
        phone: typeof emergency === 'object' ? (emergency.phone || '') : '',
        relationship: typeof emergency === 'object' ? (emergency.relationship || '') : ''
      }
    });
    setEditDialog(true);
  };

  const handleSaveProfile = async () => {
    if (!selectedCoach) return;
    
    try {
      await api.put(`/admin/coaches/${selectedCoach.id}`, editForm);
      toast.success('Coach profile updated');
      setEditDialog(false);
      fetchCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Coach',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            row.profile?.status === 'inactive' ? 'bg-slate-100' : 'bg-purple-100'
          }`}>
            <span className={`font-semibold ${
              row.profile?.status === 'inactive' ? 'text-slate-400' : 'text-purple-600'
            }`}>
              {row.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/admin/coaches/${row.id}`)}
                className={`font-medium hover:underline text-left ${row.profile?.status === 'inactive' ? 'text-slate-400' : 'text-blue-600 hover:text-blue-700'}`}
                data-testid={`coach-name-link-${row.id}`}
              >
                {row.name}
              </button>
              {row.numeric_id && (
                <span className="text-xs text-slate-400 font-mono">ID: {row.numeric_id}</span>
              )}
            </div>
            {row.display_name && row.display_name !== row.name && (
              <p className="text-xs text-purple-600 font-medium">"{row.display_name}"</p>
            )}
            <p className="text-sm text-slate-500">{row.profile?.specialization || ''}</p>
          </div>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Mail className="h-3 w-3" />
          {row.email}
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => row.phone ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Phone className="h-3 w-3" />
          {row.phone}
        </div>
      ) : <span className="text-slate-400">-</span>
    },
    {
      key: 'profile.hourly_rate',
      label: 'Hourly Rate',
      sortable: true,
      render: (row) => row.profile?.hourly_rate ? (
        <span className="font-medium text-green-600">{cs()}{row.profile.hourly_rate}/hr</span>
      ) : <span className="text-slate-400">Not set</span>
    },
    {
      key: 'profile.status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ],
      render: (row) => {
        const status = row.profile?.status || 'active';
        return (
          <Badge className={status === 'active' 
            ? 'bg-green-100 text-green-700 border-0' 
            : 'bg-slate-100 text-slate-500 border-0'
          }>
            {status === 'active' ? <UserCheck className="h-3 w-3 mr-1" /> : <UserX className="h-3 w-3 mr-1" />}
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Joined',
      sortable: true,
      render: (row) => new Date(row.created_at).toLocaleDateString()
    }
  ];

  const actions = (row) => {
    const isInactive = row.profile?.status === 'inactive';
    
    return (
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/coaches/${row.id}`)}
          data-testid={`view-coach-${row.id}`}
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openEditDialog(row)}
          data-testid={`edit-coach-${row.id}`}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        {isInactive ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleReactivate(row.id)}
            className="text-green-600 hover:text-green-700 hover:bg-green-50"
            data-testid={`reactivate-coach-${row.id}`}
            title="Reactivate Coach"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openDeleteDialog(row)}
            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            data-testid={`deactivate-coach-${row.id}`}
            title="Deactivate Coach"
          >
            <UserX className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Coaches">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Coaches">
      <div className="space-y-6" data-testid="coaches-page">
        <div className="space-y-3">
          <p className="text-slate-600">Manage coaching staff</p>
          <div className="flex flex-wrap items-center gap-3">
            <ColumnConfig
              columns={columns}
              visibleColumns={visibleColumns}
              onColumnChange={setVisibleColumns}
              storageKey="admin-coaches-columns"
              requiredColumns={['name']}
            />
            <BulkActions
              entityName="Coaches"
              exportEndpoint="/admin/coaches/export"
              uploadEndpoint="/admin/coaches/bulk-upload"
              onUploadComplete={fetchCoaches}
              sampleFields={['name', 'email', 'phone', 'display_name', 'hourly_rate', 'specialization', 'bio']}
            />
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-coach-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Coach
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add New Coach</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Coach name"
                    required
                    data-testid="coach-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="coach@example.com"
                    required
                    data-testid="coach-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Initial password"
                    required
                    data-testid="coach-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Phone (Optional)</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    data-testid="coach-phone-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="save-coach-btn">
                    Add Coach
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Status Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Active
              <Badge variant="secondary" className="ml-1">{coaches.filter(c => (c.profile?.status || 'active') === 'active').length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="inactive" className="flex items-center gap-2">
              <UserX className="h-4 w-4" />
              Inactive
              <Badge variant="secondary" className="ml-1">{coaches.filter(c => c.profile?.status === 'inactive').length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="all">
              All
              <Badge variant="secondary" className="ml-1">{coaches.length}</Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <DataTable
              data={getFilteredCoaches()}
              columns={columns}
              visibleColumns={visibleColumns}
              actions={actions}
              emptyIcon={GraduationCap}
              emptyMessage={activeTab === 'inactive' ? "No inactive coaches" : "No coaches yet. Add your first coach!"}
              searchable={true}
              searchFields={['name', 'email', 'phone']}
              testIdPrefix="coaches"
            />
          </CardContent>
        </Card>

        {/* Coach Details Dialog */}
        <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-purple-500" />
                Coach Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedCoach && (
              <div className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className="bg-gradient-to-r from-purple-50 to-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-2xl">
                          {selectedCoach.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-bold text-slate-900">
                          {selectedCoach.name}
                        </h3>
                        {selectedCoach.display_name && selectedCoach.display_name !== selectedCoach.name && (
                          <p className="text-purple-600 font-medium">"{selectedCoach.display_name}"</p>
                        )}
                        <p className="text-slate-600">{selectedCoach.profile?.specialization || 'Not specified'}</p>
                        <p className="text-slate-600 flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {selectedCoach.email}
                        </p>
                        {selectedCoach.phone && (
                          <p className="text-slate-600 flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            {selectedCoach.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" onClick={() => { setDetailsDialog(false); openEditDialog(selectedCoach); }}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Hourly Rate
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl font-bold text-green-600">
                        {cs()}{selectedCoach.profile?.hourly_rate || 0}/hr
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Classes
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl font-bold text-blue-600">
                        {coachClasses.length}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Experience
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl font-bold text-slate-600">
                        {selectedCoach.profile?.experience_years || 0} yrs
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Address */}
                {selectedCoach.profile?.address && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Address
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {typeof selectedCoach.profile.address === 'object' ? (
                        <div className="text-sm">
                          {selectedCoach.profile.address.street && <p>{selectedCoach.profile.address.street}</p>}
                          <p>
                            {selectedCoach.profile.address.city && selectedCoach.profile.address.city}
                            {selectedCoach.profile.address.state && `, ${selectedCoach.profile.address.state}`}
                            {selectedCoach.profile.address.zip_code && ` ${selectedCoach.profile.address.zip_code}`}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm">{selectedCoach.profile.address}</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Emergency Contact */}
                {selectedCoach.profile?.emergency_contact && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Emergency Contact</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {typeof selectedCoach.profile.emergency_contact === 'object' ? (
                        <div className="text-sm">
                          {selectedCoach.profile.emergency_contact.name && (
                            <p className="font-medium">{selectedCoach.profile.emergency_contact.name}</p>
                          )}
                          {selectedCoach.profile.emergency_contact.phone && (
                            <p>{selectedCoach.profile.emergency_contact.phone}</p>
                          )}
                          {selectedCoach.profile.emergency_contact.relationship && (
                            <p className="text-slate-500">{selectedCoach.profile.emergency_contact.relationship}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm">{selectedCoach.profile.emergency_contact}</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Assigned Classes */}
                <div>
                  <h4 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    Assigned Classes ({coachClasses.length})
                  </h4>
                  {loadingDetails ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                    </div>
                  ) : coachClasses.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4">No classes assigned</p>
                  ) : (
                    <div className="space-y-2">
                      {coachClasses.map(cls => (
                        <div key={cls.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{cls.name}</p>
                            <p className="text-sm text-slate-500">
                              {cls.schedule_day} • {cls.start_time} - {cls.end_time}
                            </p>
                          </div>
                          <Badge className={cls.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                            {cls.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Member Since */}
                <div className="text-sm text-slate-500 pt-4 border-t">
                  Member since: {new Date(selectedCoach.created_at).toLocaleDateString()}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Coach Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Pencil className="h-5 w-5 text-purple-500" />
                Edit Coach Profile
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Display Name - highlighted */}
              <div className="p-3 bg-purple-50 rounded-lg space-y-2">
                <Label className="text-purple-700 font-medium">Display Name (for assignments)</Label>
                <Input
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                  placeholder="e.g., Coach Benjie"
                  data-testid="edit-coach-display-name"
                />
                <p className="text-xs text-purple-600">
                  This name will be shown in class assignments and schedules instead of the full name.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    data-testid="edit-coach-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    data-testid="edit-coach-phone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.hourly_rate}
                    onChange={(e) => setEditForm({ ...editForm, hourly_rate: parseFloat(e.target.value) || 0 })}
                    data-testid="edit-coach-rate"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Experience (years)</Label>
                  <Input
                    type="number"
                    value={editForm.experience_years}
                    onChange={(e) => setEditForm({ ...editForm, experience_years: parseInt(e.target.value) || 0 })}
                    data-testid="edit-coach-experience"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Specialization</Label>
                <Input
                  value={editForm.specialization}
                  onChange={(e) => setEditForm({ ...editForm, specialization: e.target.value })}
                  placeholder="e.g., Batting Coach, Bowling Coach"
                  data-testid="edit-coach-specialization"
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-700 mb-3">Address</h4>
                <div className="space-y-3">
                  <Input
                    value={editForm.address.street}
                    onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, street: e.target.value }})}
                    placeholder="Street Address"
                    data-testid="edit-coach-street"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      value={editForm.address.city}
                      onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, city: e.target.value }})}
                      placeholder="City"
                      data-testid="edit-coach-city"
                    />
                    <Input
                      value={editForm.address.state}
                      onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, state: e.target.value }})}
                      placeholder="State"
                      data-testid="edit-coach-state"
                    />
                    <Input
                      value={editForm.address.zip_code}
                      onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, zip_code: e.target.value }})}
                      placeholder="ZIP"
                      data-testid="edit-coach-zip"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-700 mb-3">Emergency Contact</h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={editForm.emergency_contact.name}
                      onChange={(e) => setEditForm({ ...editForm, emergency_contact: { ...editForm.emergency_contact, name: e.target.value }})}
                      placeholder="Contact Name"
                      data-testid="edit-coach-emergency-name"
                    />
                    <Input
                      value={editForm.emergency_contact.phone}
                      onChange={(e) => setEditForm({ ...editForm, emergency_contact: { ...editForm.emergency_contact, phone: e.target.value }})}
                      placeholder="Contact Phone"
                      data-testid="edit-coach-emergency-phone"
                    />
                  </div>
                  <Input
                    value={editForm.emergency_contact.relationship}
                    onChange={(e) => setEditForm({ ...editForm, emergency_contact: { ...editForm.emergency_contact, relationship: e.target.value }})}
                    placeholder="Relationship (e.g., Spouse, Parent)"
                    data-testid="edit-coach-emergency-relationship"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleSaveProfile} data-testid="save-coach-profile-btn">
                <Save className="h-4 w-4 mr-1" />
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deactivate Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate Coach</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to deactivate <strong>{coachToDelete?.name}</strong>? 
                They will no longer appear in the active list, but their history will be preserved.
                You can reactivate them at any time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDeactivate(coachToDelete?.id)}
                disabled={deleting}
                className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
              >
                {deleting ? 'Deactivating...' : 'Deactivate'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default Coaches;
